
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Story, BlogPost } from '../domain/types';
import { contentRepository } from '../data/contentRepository';
import { contentService } from '../domain/services/contentService';

interface ContentContextType {
    stories: Story[];
    activeStories: Story[];
    posts: BlogPost[];
    
    // Stories by Shop
    getActiveStoriesByShop?: (shopId: string) => Story[];
    
    // Updated Signatures using Service
    createStory: (storyData: Partial<Story>, lifeTimeHours: number, user: {name: string, avatar: string}) => Promise<boolean>;
    deleteStory: (id: string) => Promise<void>;
    archiveStory: (id: string) => void;
    restoreStory: (id: string) => void;
    markStoryViewed: (id: string) => void;
    
    savePost: (postData: Partial<BlogPost>, user: {name: string}) => Promise<boolean>;
    deletePost: (id: string) => void;
    togglePostReaction: (postId: string, userId: string) => void;
}

const ContentContext = createContext<ContentContextType | null>(null);

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [stories, setStories] = useState<Story[]>([]);
    const [posts, setPosts] = useState<BlogPost[]>([]);

    useEffect(() => {
        // Real-time subscription for Stories
        const unsubStories = contentRepository.subscribeToStories(setStories);
        
        // Real-time subscription for Posts
        const unsubPosts = contentRepository.subscribeToPosts(setPosts);

        return () => {
            unsubStories();
            unsubPosts();
        };
    }, []);

    // --- STORY LOGIC ---
    const activeStories = useMemo(() => stories.filter(s => s.status === 'active'), [stories]);

    const getActiveStoriesByShop = useCallback((shopId: string): Story[] => {
        return stories.filter(s => 
            s.status === 'active' && 
            s.shopId === shopId
        );
    }, [stories]);

    const createStory = useCallback(async (storyData: Partial<Story>, lifeTimeHours: number, user: {name: string, avatar: string}) => {
        const result = await contentService.createStory(storyData, lifeTimeHours, user);
        return result.success;
    }, []);

    const deleteStory = useCallback((id: string) => {
        return contentRepository.deleteStory(id);
    }, []);

    const archiveStory = useCallback((id: string) => {
        contentRepository.updateStoryStatus(id, 'archived');
    }, []);

    const restoreStory = useCallback((id: string) => {
        contentRepository.updateStoryStatus(id, 'active');
    }, []);

    const markStoryViewed = useCallback((id: string) => {
        // Placeholder: Track story view - to be implemented with analytics
    }, []);

    // --- BLOG LOGIC ---
    const savePost = useCallback(async (postData: Partial<BlogPost>, user: {name: string}) => {
        const result = await contentService.createOrUpdatePost(postData, user);
        return result.success;
    }, []);

    const deletePost = useCallback((id: string) => {
        contentRepository.deletePost(id);
    }, []);

    const togglePostReaction = useCallback((postId: string, userId: string) => {
        // Placeholder: Toggle post reaction - to be implemented
    }, []);

    const value = useMemo(() => ({
        stories, activeStories, posts, getActiveStoriesByShop,
        createStory, deleteStory, archiveStory, restoreStory, markStoryViewed,
        savePost, deletePost, togglePostReaction
    }), [
        stories, activeStories, posts, getActiveStoriesByShop,
        createStory, deleteStory, archiveStory, restoreStory, markStoryViewed,
        savePost, deletePost, togglePostReaction
    ]);

    return (
        <ContentContext.Provider value={value}>
            {children}
        </ContentContext.Provider>
    );
};

export const useContent = () => {
    const context = useContext(ContentContext);
    if (!context) throw new Error("useContent must be used within ContentProvider");
    return context;
};
